import type { ListingContact, RealEstateContact } from "../../../../@types/sharedSchema";
import { isSamePhone } from "./sellerPhone";

/**
 * The PURE half of the listing's seller section: the shape the form edits, and
 * the mappers between it and what gets stored.
 *
 * Split out of `ListingSellerSection.tsx` for the same reason
 * `contactWriteRules.ts` is split from `contactsWrite.service.ts` on the server:
 * the component imports `useContactByPhone`, which imports `firebaseConfig`,
 * which is unloadable from a unit test. The rules that decide WHAT GETS WRITTEN
 * are exactly the ones worth testing without a database — and here a mistake
 * writes a contact under an id nobody can reach, not merely a wrong-looking
 * screen. See `sellerPhone.test.ts`.
 *
 * `./sellerPhone` is the one import, and it is the same purity tier: no firebase,
 * no DOM, no hooks, and already loaded by that same test file. Phone comparison
 * belongs there and nowhere else — a second normaliser is how the two halves of
 * this feature start disagreeing about whether two spellings are one number.
 */

export type ListingSellerDraft = {
  firstName: string;
  lastName: string;
  phone: string;
  /**
   * The contact document this seller IS.
   *
   * Set two ways: resolved from storage (a previous save stamped it), or chosen
   * by the realtor in the picker. Never typed by hand, and never derived from the
   * phone here — `persistListingSeller` does that at save time, from the phone as
   * finally typed.
   *
   * ⚠ A CONTACT'S DOCUMENT ID *IS* ITS PHONE, while a LISTING stores the seller's
   * phone in the `callerPhoneNumber` FIELD. That is the whole reason the picker
   * needs an explicit two-way adapter (`sellerDraftAsPickerContact` /
   * `applyPickedContactToDraft`) rather than a cast: the multi-picker this section
   * replaced lost its chips' numbers exactly by skipping it.
   */
  contactId?: string;
  /**
   * `listingContacts` entries 1..n, carried through the round trip UNTOUCHED.
   *
   * The form edits ONE seller — entry [0] — but the field is genuinely an array
   * and was edited by a MULTI picker (`NewMultipleContactPicker`) for as long as
   * this form has existed, so stored listings really do hold more than one entry.
   * `createOrUpdateListing` writes whatever array it is handed, so dropping the
   * tail here would silently delete co-owners on the next save of any listing
   * that has them — an invisible data loss, on a save the realtor made about
   * something else entirely.
   */
  extraContacts?: ListingContact[];
  /**
   * "Also add this person to my contacts."
   *
   * Recording who the owner is and adding someone to the CRM are different acts,
   * and this field is the second one. It is opt-in: any typed phone used to mint a
   * contact silently, which is wrong on a qualification call where the number is
   * often a Yad2 mask, the "owner" may turn out to be a tenant, and the deal may
   * not survive the call.
   *
   * ⚠ IT IS ONLY EVER A QUESTION ABOUT A **NEW** CONTACT. When the phone already
   * belongs to one, the listing links to that person regardless — linking is not
   * creating, and there is nothing to opt into. It lives on the draft rather than
   * in component state so the answer survives a re-render and travels with the
   * seller into the save.
   *
   * NOT stored on the listing: `listingContactsFromSellerDraft` deliberately does
   * not emit it. It is an instruction to `persistListingSeller` for THIS save, not
   * a property of the seller.
   */
  createContact?: boolean;
};

/** A listing contact that says something. All-blank rows are treated as absent. */
const hasSubstance = (contact?: ListingContact | null): boolean =>
  Boolean(
    contact &&
      ((contact.callerFirstName ?? "").trim() ||
        (contact.callerLastName ?? "").trim() ||
        (contact.callerPhoneNumber ?? "").trim())
  );

/**
 * `ListingContact[]` (as stored) → the form's draft.
 *
 * Entry [0] becomes the editable seller; entries 1..n ride along in
 * `extraContacts` so the next save preserves them.
 *
 * An empty stored `contactId` reads as unlinked — `linkListingContact` clears a
 * link by writing `''`, since an array element's key cannot be
 * `FieldValue.delete()`d.
 */
export function sellerDraftFromListingContacts(contacts?: ListingContact[] | null): ListingSellerDraft {
  const list = contacts ?? [];
  const first = list[0];
  // All-blank tail rows are dropped rather than carried: they are the residue of
  // a picker that wrote empty slots, and re-storing them only forces every reader
  // to keep treating `{'','',''}` as absent.
  const extras = list.slice(1).filter(hasSubstance);
  return {
    firstName: first?.callerFirstName ?? "",
    lastName: first?.callerLastName ?? "",
    phone: first?.callerPhoneNumber ?? "",
    contactId: first?.contactId || undefined,
    ...(extras.length ? { extraContacts: extras } : {})
  };
}

/**
 * The form's draft → `ListingContact[]` for the write.
 *
 * Returns null when nothing was entered anywhere, so an untouched seller section
 * stores `listingContacts: null` rather than a row of empty strings that every
 * reader would then have to treat as absent.
 *
 * A name with no phone is kept: recording WHO the owner is has value on its own,
 * and it is a normal state mid-call.
 *
 * ⚠ THE EDITED SELLER IS ALWAYS INDEX 0 WHEN IT EXISTS. `persistListingSeller`
 * stamps the resolved `contactId` onto index 0 (`linkListingContact(..., 0)`), so
 * a shape that ever put a co-owner there would link the listing to the wrong
 * person. Clearing the primary seller outright DOES promote entry 1 — that is the
 * plain meaning of removing it — and is safe because a cleared entry carries no
 * phone, and `persistListingSeller` returns early without stamping anything when
 * there is no phone.
 */
export function listingContactsFromSellerDraft(draft: ListingSellerDraft): ListingContact[] | null {
  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  const phone = draft.phone.trim();
  const extras = (draft.extraContacts ?? []).filter(hasSubstance);

  if (!firstName && !lastName && !phone) return extras.length ? extras : null;

  return [
    {
      callerFirstName: firstName,
      callerLastName: lastName,
      callerPhoneNumber: phone,
      ...(draft.contactId ? { contactId: draft.contactId } : {})
    },
    ...extras
  ];
}

/**
 * The subset of a contact this section reads. `id` IS the phone — see the note on
 * `contactId` above.
 */
export type PickedContactLike = Pick<RealEstateContact, "id" | "callerFirstName" | "callerLastName">;

/**
 * The draft → the shape `NewContactPicker` wants as its `value`.
 *
 * ⚠ THIS ADAPTER IS NOT OPTIONAL. The picker's `isOptionEqualToValue` compares
 * `option.id === value.id`, and a stored `ListingContact` has no `id` at all — so
 * feeding one in raw compares `undefined === undefined`, every option matches the
 * saved seller, and the chip renders blank. That exact bug was fixed once already
 * (upstream `1ffe674b`) in the picker this section replaced.
 *
 * Returns null when the seller is not a known contact: there is nothing for the
 * picker to be showing, and a value the picker cannot resolve is worse than none.
 */
export function sellerDraftAsPickerContact(draft: ListingSellerDraft): PickedContactLike | null {
  if (!draft.contactId) return null;
  return {
    id: draft.contactId,
    callerFirstName: draft.firstName,
    callerLastName: draft.lastName
  };
}

export type ApplyPickedContactResult = {
  next: ListingSellerDraft;
  /**
   * The identity the pick DISCARDED, when it discarded anything. Present only
   * when the realtor had typed something different — so the UI can offer an undo
   * instead of silently overwriting her work, and stay quiet in the ordinary case
   * where she picked before typing.
   */
  replaced?: Pick<ListingSellerDraft, "firstName" | "lastName" | "phone">;
};

/**
 * A contact the realtor chose (from the picker, or one `EditContactDialog` just
 * created) → the draft.
 *
 * ## The rule for the LISTING side, stated once
 *
 * `persistListingSeller` already protects the CONTACT side: it merges only the
 * link and the customer type onto an existing contact, never its identity, because
 * she may have spent months filling that card in. This is the mirror rule, and it
 * is deliberately NOT the same one:
 *
 *  - **Picking replaces the listing's name and phone.** Choosing a person from the
 *    book is an act of identification, not an autofill hint. Filling only the
 *    EMPTY fields was the alternative and it is worse: typing "משה" and then
 *    picking "דוד לוי" would store "משה לוי", a person who does not exist.
 *  - **…except a name the contact does not have.** A phone-only contact card has
 *    no name to overwrite with, so the typed one survives. That is not a mismatch:
 *    the listing records what she knows, and nothing rewrites the contact.
 *  - **A discarded value is reported, never lost silently** (`replaced`), so the
 *    caller can offer one-click undo.
 *
 * The listing's typed name stays the LISTING's own record either way — it is never
 * written back onto the contact.
 */
export function applyPickedContactToDraft(
  draft: ListingSellerDraft,
  contact: PickedContactLike | null | undefined
): ApplyPickedContactResult {
  const contactId = contact?.id?.trim();
  // No id means no contact: the id IS the phone, so there is nothing to identify
  // the person by and nothing to key a write on.
  if (!contactId) return { next: draft };

  const first = (contact?.callerFirstName ?? "").trim();
  const last = (contact?.callerLastName ?? "").trim();
  const contactHasName = Boolean(first || last);

  const typedFirst = draft.firstName.trim();
  const typedLast = draft.lastName.trim();
  const typedPhone = draft.phone.trim();

  const nameDiscarded = contactHasName && Boolean(typedFirst || typedLast) && (typedFirst !== first || typedLast !== last);
  const phoneDiscarded = Boolean(typedPhone) && !isSamePhone(typedPhone, contactId);

  return {
    next: {
      ...draft,
      firstName: contactHasName ? first : draft.firstName,
      lastName: contactHasName ? last : draft.lastName,
      phone: contactId,
      contactId
    },
    ...(nameDiscarded || phoneDiscarded
      ? { replaced: { firstName: draft.firstName, lastName: draft.lastName, phone: draft.phone } }
      : {})
  };
}

/**
 * "This is no longer that contact."
 *
 * Drops the resolved id and KEEPS the typed identity. Clearing the picker means
 * she is no longer pointing at a contact document, not that she has forgotten who
 * the owner is — and a name and number with no contact behind them is a perfectly
 * good listing record, which is the whole point of the free-text path.
 */
export function clearPickedContact(draft: ListingSellerDraft): ListingSellerDraft {
  if (!draft.contactId) return draft;
  const { contactId, ...rest } = draft;
  return rest;
}
